import { LogOut, Settings, User, Building2, Check, Users, FileText } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/components/auth-context"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ScrollArea } from "@/components/ui/scroll-area"

export function AvatarDropdown() {
    const { t } = useTranslation()
    const { user, signOut, organizations, organization, switchOrganization } = useAuth()
    const navigate = useNavigate()

    const handleSignOut = async () => {
        await signOut()
        navigate("/login")
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Avatar className="h-8 w-8 md:h-10 md:w-10 ring-2 ring-primary/10 transition-transform hover:scale-110 cursor-pointer">
                    <AvatarImage
                        src={user?.user_metadata?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.email}`}
                    />
                    <AvatarFallback>{user?.email?.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                            {user?.user_metadata?.full_name || t('avatarDropdown.user')}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                            {user?.email}
                        </p>
                    </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    {t('avatarDropdown.spaces')}
                </DropdownMenuLabel>
                <ScrollArea className="max-h-[200px] w-full rounded-md p-1">
                    {organizations.map((org) => {
                        const isPersonal = org.type === 'personal'
                        const displayName = isPersonal
                            ? (user?.user_metadata?.full_name
                                ? t('avatarDropdown.personalSpacePrefix', { name: user?.user_metadata?.full_name?.split(' ')[0] })
                                : t('avatarDropdown.personalSpaceDefault'))
                            : org.name

                        return (
                            <DropdownMenuItem
                                key={org.id}
                                onClick={() => switchOrganization(org.id)}
                                className="cursor-pointer justify-between"
                            >
                                <div className="flex items-center">
                                    {isPersonal ? (
                                        <User className="mr-2 h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <Building2 className="mr-2 h-4 w-4 text-blue-500" />
                                    )}
                                    <span className={organization?.id === org.id ? "font-medium truncate max-w-[120px]" : "truncate max-w-[120px]"}>
                                        {displayName}
                                    </span>
                                </div>
                                {organization?.id === org.id && (
                                    <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                                )}
                            </DropdownMenuItem>
                        )
                    })}
                </ScrollArea>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => navigate("/team")} className="cursor-pointer hover:bg-primary/10">
                    <Users className="mr-2 h-4 w-4 text-primary" />
                    <span>{t('avatarDropdown.manageTeam')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/reports")} className="cursor-pointer hover:bg-primary/10">
                    <FileText className="mr-2 h-4 w-4 text-primary" />
                    <span>{t('avatarDropdown.centralReports')}</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>{t('avatarDropdown.settings')}</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                    onClick={handleSignOut}
                    className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('avatarDropdown.signOut')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
